import { useState, type FormEvent } from "react";
import { toast } from "sonner";
import { errorMessage, fieldErrors } from "../../../api/client";
import { adminApi } from "../../../api/resources";
import { Modal } from "../../../components/Overlay";
import { Button, ErrorText, Field, TextInput } from "../../../components/ui";

export function InviteModal({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: (url: string | null) => void }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});

  const close = () => {
    setName("");
    setEmail("");
    setError(null);
    setFields({});
    onClose();
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const local: Record<string, string> = {};
    if (!name.trim()) local.full_name = "Name is required";
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) local.email = "Enter a valid email address";
    setFields(local);
    if (Object.keys(local).length) return;
    setBusy(true);
    setError(null);
    try {
      const res = await adminApi.createInvitation(name, email);
      toast.success(`Invitation sent to ${res.invitation.email}`);
      onCreated(res.registration_url);
      close();
    } catch (err) {
      const f = fieldErrors(err);
      if (Object.keys(f).length) setFields(f);
      else setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={close} title="Invite employee" testId="invite-modal">
      <form onSubmit={submit} className="space-y-5" noValidate>
        <Field label="Full name" htmlFor="invite-name">
          <TextInput id="invite-name" value={name} onChange={(e) => setName(e.target.value)} data-testid="invite-name-input" />
          {fields.full_name && <p data-testid="invite-name-error" className="text-xs text-red-300">{fields.full_name}</p>}
        </Field>
        <Field label="Email" htmlFor="invite-email">
          <TextInput id="invite-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="invite-email-input" />
          {fields.email && <p data-testid="invite-email-error" className="text-xs text-red-300">{fields.email}</p>}
        </Field>
        <ErrorText message={error} testId="invite-error" />
        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="secondary" onClick={close} data-testid="invite-cancel-button">Cancel</Button>
          <Button type="submit" loading={busy} data-testid="invite-submit-button">Send invitation</Button>
        </div>
      </form>
    </Modal>
  );
}

export function PreviewLink({ url, onDismiss }: { url: string | null; onDismiss: () => void }) {
  if (!url) return null;
  return (
    <div data-testid="preview-registration-link" className="mb-6 rounded-sm border border-warn/40 bg-warn/5 p-4 text-sm">
      <p className="label-caps text-warn">Preview only · registration link</p>
      <a href={url} target="_blank" rel="noreferrer" data-testid="preview-registration-url" className="mt-2 block break-all font-mono text-xs text-white underline hover:text-signal">
        {url}
      </a>
      <button type="button" onClick={onDismiss} data-testid="preview-registration-dismiss" className="mt-2 text-xs text-zinc-400 hover:text-white">Dismiss</button>
    </div>
  );
}
